/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";
import Emitter, { EventTypes } from "../libs/Emitter";

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  names: {
    signUpSignIn: "B2C_1_NTNMath_SignUpIn",
    forgotPassword: "B2C_1_NTNMath_password_reset",
  },
  authorities: {
    signUpSignIn: {
      authority: `${process.env.REACT_APP_AUTHORITY}/B2C_1_NTNMath_SignUpIn`,
    },
    forgotPassword: {
      authority: `${process.env.REACT_APP_AUTHORITY}/B2C_1_NTNMath_password_reset`,
    },
  },
  authorityDomain: `${process.env.REACT_APP_AUTHORITY_DOMAIN}`,
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId: `${process.env.REACT_APP_CLIENT_ID}`, // This is the ONLY mandatory field that you need to supply.
    authority: b2cPolicies.authorities.signUpSignIn.authority, // Use a sign-up/sign-in user-flow as a default authority
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri: "/", // Points to window.location.origin. You must register this URI on Azure Portal/App Registration.
    postLogoutRedirectUri: "/", // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: "sessionStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  environment: "ntnmath.b2clogin.com",
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.log("message", message);
            if (message.indexOf("msal:loginFailure") > -1) {
              console.log("login failed...");
            }

            if (message.indexOf("msal:logoutSuccess") > -1) {
              // console.log("logout successfull...");
              Emitter.emit(EventTypes.NTN_LOGOUT_SUCCESS);
            }

            if (message.indexOf("msal:loginSuccess") > -1) {
              //const sessionKey = window.sessionStorage.key(0);
              // console.log("sessionKey", sessionKey);
              const sessionKey = findSessionToken();
              if (sessionKey) {
                const sessionData = JSON.parse(sessionKey);
                if (
                  (sessionData.credentialType === "AccessToken" ||
                    sessionData.credentialType === "RefreshToken") &&
                  sessionData.environment === msalConfig.environment &&
                  sessionData.clientId === msalConfig.auth.clientId
                ) {
                  // console.log("Emitting token data", sessionData);
                  Emitter.emit(EventTypes.NTN_LOGIN_SUCCESS, sessionData);
                } else {
                  console.log("invalid token");
                }
              }
            }
            // clear, storage and cache
            if (message.indexOf("msal:acquireTokenFailure") > -1) {
              Emitter.emit(EventTypes.NTN_TOKEN_FAILURE);
            }

            // if (message.indexOf("useAccount - Updating account") > -1) {
            //   console.log("check token");
            //   const sessionKey = findSessionToken();
            //   console.log("sessionKey", sessionKey);
            //   if (sessionKey) {
            //     let sessionData = null;
            //     try {
            //       sessionData = JSON.parse(sessionKey);
            //       console.log("sessionData", sessionData);
            //     } catch (error) {}

            //     if (
            //       (sessionData?.credentialType === "AccessToken" ||
            //         sessionData?.credentialType === "RefreshToken") &&
            //       sessionData?.environment === msalConfig.environment &&
            //       sessionData?.clientId === msalConfig.auth.clientId
            //     ) {
            //       Emitter.emit(EventTypes.NTN_ACQUIRETOKEN_SUCCESS);
            //     }
            //   }
            // }
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
    },
  },
  scopes: [
    "openid",
    `https://ntnmath.onmicrosoft.com/${process.env.REACT_APP_CLIENT_ID}/api_scope`,
  ],
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [
    "openid",
    `https://ntnmath.onmicrosoft.com/${process.env.REACT_APP_CLIENT_ID}/api_scope`,
  ],
};

export const logoutRequest = {
  postLogoutRedirectUri: "/",
  mainWindowRedirectUri: "/",
};
/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "login_hint" property.
 */
export const silentRequest = {
  scopes: [
    "openid",
    `https://ntnmath.onmicrosoft.com/${process.env.REACT_APP_CLIENT_ID}/api_scope`,
  ],
  loginHint: "example@domain.net",
};

const findSessionToken = () => {
  let my_default_key = window.sessionStorage.key(0);
  const accesstoken_key = `${msalConfig.environment}-accesstoken-${process.env.REACT_APP_CLIENT_ID}`;
  const keysList = Object.keys(window.sessionStorage);
  let my_accesstoken_key = keysList.find(
    (k) => k.indexOf(accesstoken_key) !== -1
  );

  const refresh_token_key = `${msalConfig.environment}-refreshtoken-${process.env.REACT_APP_CLIENT_ID}`;

  let my_refresh_key = keysList.find(
    (k) => k.indexOf(refresh_token_key) !== -1
  );

  if (my_accesstoken_key) {
    return window.sessionStorage.getItem(my_accesstoken_key);
  } else if (my_refresh_key) {
    return window.sessionStorage.getItem(my_refresh_key);
  } else {
    return window.sessionStorage.getItem(my_default_key);
  }
};
